import { Maximize, Minimize, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ImagePreviewDialogProps {
  visible: boolean;
  title: string;
  imageUrl: string | null;
  error?: boolean;
  onClose: () => void;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.1;
const WHEEL_ZOOM_SENSITIVITY = 0.001;

export const ImagePreviewDialog: FC<ImagePreviewDialogProps> = ({
  visible,
  title,
  imageUrl,
  error = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const constrainPan = useCallback((newPanX: number, newPanY: number, currentZoom: number) => {
    if (!imageRef.current || !containerRef.current) {
      return { x: newPanX, y: newPanY };
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const imgRect = imageRef.current.getBoundingClientRect();

    // Image natural size; scaled size = natural * zoom (imgRect is already scaled by zoom)
    const imgWidth = imgRect.width / currentZoom;
    const imgHeight = imgRect.height / currentZoom;
    const scaledWidth = imgWidth * currentZoom;
    const scaledHeight = imgHeight * currentZoom;

    // Container dimensions (accounting for padding)
    const containerWidth = containerRect.width - 32; // 32px = 2 * 16px padding
    const containerHeight = containerRect.height - 32;

    // Pan bounds: image center is at (containerWidth/2 + pan.x, containerHeight/2 + pan.y).
    // Left edge = containerWidth/2 - scaledWidth/2 + pan.x; keep it in [0, containerWidth - scaledWidth].
    // So pan.x in [(containerWidth - scaledWidth)/2, (scaledWidth - containerWidth)/2] (minX <= maxX when zoomed).
    const minX = (containerWidth - scaledWidth) / 2;
    const maxX = (scaledWidth - containerWidth) / 2;
    const minY = (containerHeight - scaledHeight) / 2;
    const maxY = (scaledHeight - containerHeight) / 2;

    return {
      x: Math.max(minX, Math.min(maxX, newPanX)),
      y: Math.max(minY, Math.min(maxY, newPanY)),
    };
  }, []);

  // Reset zoom and pan when dialog opens/closes or image changes
  useEffect(() => {
    if (visible && imageUrl) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  }, [visible, imageUrl]);

  // Constrain pan when zoom changes
  useEffect(() => {
    if (zoom !== 1 && imageRef.current && containerRef.current) {
      setPan((currentPan) => constrainPan(currentPan.x, currentPan.y, zoom));
    }
  }, [zoom, constrainPan]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (visible) {
      // Store original overflow style
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [visible]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP * 5, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - ZOOM_STEP * 5, MIN_ZOOM));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!imageRef.current || !containerRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const delta = e.deltaY * WHEEL_ZOOM_SENSITIVITY;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom - delta));

    if (newZoom !== zoom) {
      // Get mouse position relative to container
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Container center
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Calculate zoom point relative to image center
      const zoomPointX = (mouseX - centerX - pan.x) / zoom;
      const zoomPointY = (mouseY - centerY - pan.y) / zoom;

      // Calculate new pan to keep zoom point under cursor
      const newPanX = mouseX - centerX - zoomPointX * newZoom;
      const newPanY = mouseY - centerY - zoomPointY * newZoom;

      // Constrain pan to boundaries
      const constrained = constrainPan(newPanX, newPanY, newZoom);

      setZoom(newZoom);
      setPan(constrained);
    }
  };

  // Prevent page scroll when wheel event happens anywhere in the dialog
  const handleDialogWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Only prevent if we're over the image container
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      if (mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom > 1 && e.button === 0) {
      setIsPanning(true);
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setPanStart({
          x: e.clientX - rect.left - pan.x,
          y: e.clientY - rect.top - pan.y,
        });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPanning && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newPanX = e.clientX - rect.left - panStart.x;
      const newPanY = e.clientY - rect.top - panStart.y;
      const constrained = constrainPan(newPanX, newPanY, zoom);
      setPan(constrained);
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleMouseLeave = () => {
    setIsPanning(false);
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const dialogHeader = (
    <div className="flex items-center justify-between w-full pr-2">
      <span className="font-semibold text-lg">{title}</span>
      <Button
        icon={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        onClick={handleToggleFullscreen}
        text={true}
        rounded={true}
        size="small"
        title={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        tooltip={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        tooltipOptions={{ position: 'left' }}
        aria-label={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        pt={{
          root: {
            style: { outline: 'none', boxShadow: 'none' },
            className: 'focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none',
          },
        }}
      />
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={onClose}
      modal={true}
      className={`w-full ${isFullscreen ? 'max-w-full h-full' : 'max-w-4xl'}`}
      dismissableMask={true}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        root: { className: isFullscreen ? 'h-full fixed inset-0' : '' },
        content: { className: isFullscreen ? 'h-full flex flex-col' : '' },
      }}
    >
      <div className={`flex flex-col gap-4 ${isFullscreen ? 'h-full' : ''}`} onWheel={handleDialogWheel}>
        {error || !imageUrl ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-600 text-lg">Preview not available</p>
          </div>
        ) : (
          <div className="relative">
            {/* Zoom controls */}
            <div className="absolute top-2 right-2 z-10 flex gap-1 bg-white/90 backdrop-blur-sm rounded-lg p-1 shadow-lg">
              <Button
                icon={<ZoomOut size={16} />}
                onClick={handleZoomOut}
                text={true}
                rounded={true}
                size="small"
                disabled={zoom <= MIN_ZOOM}
                title={t('dialogs.zoomOut')}
                tooltip={t('dialogs.zoomOut')}
                tooltipOptions={{ position: 'left' }}
                aria-label={t('dialogs.zoomOut')}
              />
              <Button
                icon={<ZoomIn size={16} />}
                onClick={handleZoomIn}
                text={true}
                rounded={true}
                size="small"
                disabled={zoom >= MAX_ZOOM}
                title={t('dialogs.zoomIn')}
                tooltip={t('dialogs.zoomIn')}
                tooltipOptions={{ position: 'left' }}
                aria-label={t('dialogs.zoomIn')}
              />
              <Button
                icon={<RotateCcw size={16} />}
                onClick={handleResetZoom}
                text={true}
                rounded={true}
                size="small"
                disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                title={t('dialogs.resetZoom')}
                tooltip={t('dialogs.resetZoom')}
                tooltipOptions={{ position: 'left' }}
                aria-label={t('dialogs.resetZoom')}
              />
            </div>

            {/* Image container with zoom and pan */}
            <div
              ref={containerRef}
              className={`flex items-center justify-center bg-gray-50 rounded-lg p-4 overflow-hidden cursor-move ${isFullscreen ? 'flex-1' : ''}`}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              style={{ cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
            >
              <div
                className="flex items-center justify-center transition-transform duration-100"
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                }}
              >
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt={title}
                  className={`max-w-full object-contain select-none ${isFullscreen ? 'max-h-[calc(100vh-200px)]' : 'max-h-[70vh]'}`}
                  draggable={false}
                />
              </div>
            </div>

            {/* Zoom level indicator */}
            {zoom !== 1 && (
              <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded px-2 py-1 text-xs text-gray-600 shadow">
                {Math.round(zoom * 100)}%
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
